import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Loader2, Search } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { FileLink } from '@/shared/components/file-upload/FileLink';
import { formatMoney } from '@/shared/lib/money';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import {
  FacturacionFilterPopover,
  VENDEDOR_ALL,
  type ClienteOpt,
  type EstadoFilter,
} from './FacturacionFilterPopover';
import { listFacturasArca } from './api';

/**
 * Facturación — the facturas actually issued through ARCA (electronic
 * invoicing). Filter by Cliente, that client's Ventas, and Vendedor, then per
 * row see the ARCA detail (Nº comprobante, CAE, vto). Manually-uploaded
 * facturas are excluded server-side (only `cae`-bearing rows are returned).
 *
 * Lives INSIDE Reportes (`reportId === 'facturacion'`), not as its own module
 * — moved 2026-07-21. Sigue la misma forma que `CierresDeCajaReport`: un hook
 * con el estado + la query, un toolbar que el shell mete en SU fila (si no, el
 * encabezado ocupa dos renglones) y un body. El `<h1>` vive en el shell.
 *
 * Run: npx tsx src/features/ventas/lib/calc.selfcheck.ts — no cubre esto, es
 * presentación pura; la lógica de saldo que sí importa está en `calc.ts`.
 */
export type FacturacionState = ReturnType<typeof useFacturacionReport>;

export function useFacturacionReport(enabled: boolean) {
  const { code } = useActiveCountry();
  const isAr = code === 'AR';

  const [cliente, setCliente] = useState<ClienteOpt | null>(null);
  const [ventaSearch, setVentaSearch] = useState('');
  const [vendedor, setVendedor] = useState<string>(VENDEDOR_ALL);
  const [estado, setEstado] = useState<EstadoFilter>('vigentes');

  const facturasQ = useQuery({
    queryKey: ['facturacion', code],
    queryFn: listFacturasArca,
    staleTime: 30_000,
    // El hook se llama siempre desde ReportesPage (regla de hooks), así que la
    // query sólo se dispara cuando Facturación es el reporte elegido.
    enabled: isAr && enabled,
  });
  const all = facturasQ.data ?? [];

  // Estado narrows the working set first; the cliente/venta/vendedor rosters
  // AND the table are all derived from it, so the dropdowns match what's shown.
  const base = useMemo(
    () =>
      all.filter((r) =>
        estado === 'vigentes' ? !r.anulada : estado === 'anuladas' ? r.anulada : true,
      ),
    [all, estado],
  );

  // Rosters show only clientes/ventas/vendedores with a factura in the current view.
  const clientes = useMemo<ClienteOpt[]>(() => {
    const m = new Map<string, string>();
    for (const r of base) if (r.clienteId) m.set(r.clienteId, r.cliente ?? r.clienteId);
    return [...m]
      .map(([clienteId, name]) => ({ clienteId, cliente: name }))
      .sort((a, b) => a.cliente.localeCompare(b.cliente, 'es'));
  }, [base]);

  const vendedores = useMemo<string[]>(
    () =>
      [...new Set(base.map((r) => r.vendedor).filter((v): v is string => Boolean(v)))].sort((a, b) =>
        a.localeCompare(b, 'es'),
      ),
    [base],
  );

  // Venta is a free-text search over its nº cotización / venta id.
  const rows = useMemo(() => {
    const q = ventaSearch.trim().toLowerCase();
    return base.filter((r) => {
      if (cliente && r.clienteId !== cliente.clienteId) return false;
      if (vendedor !== VENDEDOR_ALL && r.vendedor !== vendedor) return false;
      if (q && !`${r.nroCotizacion ?? ''} ${r.ventaSpId ?? ''}`.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [base, cliente, vendedor, ventaSearch]);

  const totalFacturado = rows.reduce((s, r) => s + r.total, 0);
  const totalSaldo = rows.reduce((s, r) => s + r.saldo, 0);

  return {
    isAr,
    facturasQ,
    all,
    rows,
    clientes,
    vendedores,
    cliente,
    setCliente,
    ventaSearch,
    setVentaSearch,
    vendedor,
    setVendedor,
    estado,
    setEstado,
    totalFacturado,
    totalSaldo,
  };
}

/**
 * Los controles van SUELTOS dentro de la fila de toolbar del shell (si se
 * renderizan como fila propia, el encabezado ocupa dos renglones). Son dos
 * exports porque van a los lados opuestos del selector de reporte: el
 * buscador antes, Filtrar después — al extremo derecho, en el mismo lugar
 * donde lo tienen todos los demás reportes.
 */
export function FacturacionSearch({ state }: { state: FacturacionState }) {
  if (!state.isAr) return null;
  return (
    <div className="relative w-56">
      <Search
        className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        variant="filled"
        placeholder="Buscar venta…"
        className="h-12 pl-9"
        value={state.ventaSearch}
        onChange={(e) => state.setVentaSearch(e.target.value)}
        aria-label="Buscar venta"
      />
    </div>
  );
}

export function FacturacionFilter({ state }: { state: FacturacionState }) {
  if (!state.isAr) return null;
  return (
    <FacturacionFilterPopover
      clientes={state.clientes}
      vendedores={state.vendedores}
      cliente={state.cliente}
      onClienteChange={state.setCliente}
      vendedor={state.vendedor}
      onVendedorChange={state.setVendedor}
      estado={state.estado}
      onEstadoChange={state.setEstado}
    />
  );
}

export function FacturacionBody({ state }: { state: FacturacionState }) {
  const { isAr, facturasQ, all, rows, totalFacturado, totalSaldo } = state;

  if (!isAr) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-center text-sm text-muted-foreground">
        La facturación electrónica (ARCA) sólo aplica en Argentina.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* Results */}
      {facturasQ.isPending ? (
        <div className="flex flex-1 items-center justify-center rounded-2xl border bg-card text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        </div>
      ) : facturasQ.isError ? (
        <div className="flex flex-1 items-center justify-center rounded-2xl border border-destructive/30 bg-destructive/10 px-4 text-sm text-destructive">
          No se pudieron cargar las facturas.
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-2xl border bg-card text-sm text-muted-foreground">
          <FileText className="h-8 w-8" aria-hidden />
          {all.length === 0
            ? 'Todavía no hay facturas emitidas por ARCA.'
            : 'Sin facturas para los filtros aplicados.'}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 border-b bg-card text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="whitespace-nowrap px-3 py-2.5 font-medium">Fecha</th>
                  <th className="whitespace-nowrap px-3 py-2.5 font-medium">Nº Comprobante</th>
                  <th className="whitespace-nowrap px-3 py-2.5 font-medium">Tipo</th>
                  <th className="whitespace-nowrap px-3 py-2.5 font-medium">Cliente</th>
                  <th className="whitespace-nowrap px-3 py-2.5 font-medium">Venta</th>
                  <th className="whitespace-nowrap px-3 py-2.5 font-medium">Vendedor</th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">Total</th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-right font-medium">Saldo</th>
                  <th className="whitespace-nowrap px-3 py-2.5 font-medium">CAE</th>
                  <th className="whitespace-nowrap px-3 py-2.5 font-medium">Vto CAE</th>
                  <th className="whitespace-nowrap px-3 py-2.5 font-medium">Factura</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/30">
                    <td className="whitespace-nowrap px-3 py-2 align-middle">{r.fecha ?? '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle font-medium text-foreground">
                      <div className="flex items-center gap-2">
                        <span>{r.nroComprobante ?? '—'}</span>
                        {r.anulada && (
                          <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive">
                            Anulada
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle">{r.tipo}</td>
                    <td className="max-w-[16rem] truncate px-3 py-2 align-middle" title={r.cliente ?? ''}>
                      {r.cliente ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle text-muted-foreground">
                      {r.nroCotizacion ?? (r.ventaSpId ? `#${r.ventaSpId}` : '—')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle">{r.vendedor ?? '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right align-middle tabular-nums font-medium text-foreground">
                      {formatMoney(r.total, 'ARS')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right align-middle tabular-nums">
                      {r.ncTotal > 0 ? (
                        <span
                          className="text-amber-700"
                          title={`Neto de Notas de Crédito: −${formatMoney(r.ncTotal, 'ARS')}`}
                        >
                          {formatMoney(r.saldo, 'ARS')}
                        </span>
                      ) : (
                        formatMoney(r.saldo, 'ARS')
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle tabular-nums">{r.cae ?? '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2 align-middle">{r.caeVencimiento ?? '—'}</td>
                    <td className="max-w-[14rem] px-3 py-2 align-middle">
                      {r.facturaFileName && r.facturaDriveItemId ? (
                        <div className="max-w-full">
                          <FileLink
                            entityType="FacturaVenta"
                            entityId={r.id}
                            fileName={r.facturaFileName}
                            allowDelete={false}
                            compact
                          />
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">PDF no adjunto</span>
                      )}
                    </td>
                    {/* Sin columna Acciones (2026-07-21): las NC se emiten
                        desde Ventas → Ver Facturas, que es donde se ve el
                        saldo a facturar de la venta. */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Totals footer */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 border-t bg-muted/30 px-4 py-2.5 text-sm">
            <span className="text-muted-foreground">
              {rows.length} {rows.length === 1 ? 'factura' : 'facturas'}
            </span>
            <span className="ml-auto">
              Saldo total:{' '}
              <strong className="tabular-nums">{formatMoney(totalSaldo, 'ARS')}</strong>
            </span>
            <span>
              Total facturado:{' '}
              <strong className="tabular-nums">{formatMoney(totalFacturado, 'ARS')}</strong>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
